import type { AdvancedDepositsModalFormInputs } from './_types';
import {
  useApiMutation,
  useApiClient,
  showApiErrorNotification,
  useQueryClient,
} from '@vrplatform/data';
import { bankersRound, ensure, sum } from '@vrplatform/utils';
import { showWarnNotification } from '@vrplatform/theme';
import { useCallback } from 'react';

export const useAdvancedDepositsModalMutation = ({
  queryDataKey,
  closeModal,
  refetch,
}: {
  queryDataKey: any[];
  closeModal: () => void;
  refetch: () => void;
}) => {
  'use memo';

  const $api = useApiClient();

  const queryClient = useQueryClient();

  const { mutateAsync, isPending: loading } = useApiMutation(
    'put',
    '/teams/opening-balances/advanced-deposits'
  );

  const submit = useCallback(
    async (data: AdvancedDepositsModalFormInputs) => {
      const accountId =
        data.accountId ??
        (await $api
          .GET('/accounts', {
            params: {
              query: {
                type: 'ledger',
              },
            },
          })
          .then((res) => {
            if (res.error) {
              showApiErrorNotification({
                error: res.error,
                title: 'Failed to load accounts',
                defaultMessage:
                  'We failed to load the accounts. Please try again and if the issue persists, contact support.',
              });
              return undefined;
            }
            const account = res.data.data.find((x) =>
              x.assignments.includes('openingBalance')
            );
            return account?.id;
          }));

      if (!accountId) {
        showWarnNotification({
          message:
            "No opening balance account found. Please add the assignment 'Opening Balance' to an account and try again.",
        });
        return;
      }

      const normalizedData: AdvancedDepositsModalFormInputs = {
        ...data,
        reservations: data.reservations.map((reservation) => ({
          ...reservation,
          paymentCentTotal: bankersRound(reservation.paymentCentTotal, 0),
          refundCentTotal: bankersRound(reservation.refundCentTotal, 0),
        })),
      };

      const amount = sum(
        normalizedData.reservations,
        (x) => x.paymentCentTotal - x.refundCentTotal
      );

      await mutateAsync({
        body: {
          accountId,
          amount,
          reservations: normalizedData.reservations.map((r) => ({
            reservationId: r.reservationId,
            payment: r.paymentCentTotal,
            refund: r.refundCentTotal,
          })),
        },
      })
        .then(() => {
          if (normalizedData.reservations.length) {
            queryClient.setQueryData(
              queryDataKey,
              ensure<AdvancedDepositsModalFormInputs>(normalizedData)
            );
          } else {
            queryClient.invalidateQueries({
              queryKey: queryDataKey,
            });
          }
          closeModal();
          refetch();
        })
        .catch((error) => {
          showApiErrorNotification({
            error,
            title: 'Failed to submit advanced deposits',
            defaultMessage:
              'We failed to submit the advanced deposits. Please try again and if the issue persists, contact support.',
          });
        });
    },
    [mutateAsync, queryClient, queryDataKey, refetch, closeModal, $api]
  );

  return {
    submit,
    loading,
  };
};
